import { Component, OnInit, Input, ChangeDetectorRef, OnChanges, OnDestroy } from "@angular/core";
import { NeutInput } from "../neut/neut-input";
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { NeutModel, NeutUtils } from 'src/app/shared/models/neut.model';
import { NeutPosition } from '../neut/neut-position';
import { NeutDto } from 'src/app/shared/api-models/models';
import { FormComponent } from 'src/app/shared/form-component';
import { UiStateService } from 'src/app/core/services/ui-state-service';
import { BaseComponent } from 'src/app/shared/base.component';
import { filter, takeUntil } from "rxjs/operators";
import { GlaslatDto } from "../../shared/api-models/glaslatDto";
import { GraphicsService } from '../../core/services/graphics.service';
import { TooltipService } from "src/app/core/services/tooltip.service";
import { DorpelUtils } from "src/app/shared/models/dorpel-type.model";

const GLASLATTEN_CONFLICT_ERR_MESSAGE = "In een wisselvlak kan een glaslat, beglazings- of een aanslagprofiel geselecteerd worden, niet meerdere.";

@Component({
  selector: "app-daagmaat",
  templateUrl: "./daagmaat.component.html",
  styleUrls: ["./daagmaat.component.css"],
})
export class DaagmaatComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  public formGroup: FormGroup;
  public neutModel: NeutModel;
  public NeutPosition = NeutPosition;
  public isUitvoeringAvailable = false;
  private neutenDependentFields: string[] = ["neut", "daagmaat", "sponningsoort", "maatA",
    "maatZ", "glaslat", "sponningmaat", "beglazingsprofiel", "aanslagprofiel", "gemonteerd", "damverbreeding", "dubbelAanslagprofiel"];

  @Input() public input: NeutInput;
  @Input() dorpelCode: string;
  @Input() neutDescription: string;
  @Input() index: number;
  @Input() count: number;
  @Input() public dorpelType: 'Binnendorpel' | 'Buitendorpel' | 'Hefschuifdorpel';
  @Input() public isDaagmaatA: boolean;

  public glaslatten: GlaslatDto[] = [];
  public neuten: NeutDto[] = [];
  public sponningmaatOptions: any[] = [];
  private defaultSponningmaatValue: number | undefined;
  private _formComponent: FormComponent;
  public beglazingConflictError: string | null = null;
  public aanslagConflictError: string | null = null;
  public glaslatConflictError: string | null = null;

  public constructor(private formBuilder: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private uiStateService: UiStateService,
    private graphicsService: GraphicsService,
    private tooltipService: TooltipService,) {
    super();
  }

  public ngOnInit() {
    this.neutModel = this.input.model;
    this.neuten = this.input.neutenChoices.filter(neut => this.neutMatchesDorpelAndPosition(neut, this.dorpelCode));
    this.determineUitvoeringAvailability();

    if (this.dorpelCode) {
      this.setSponningmaatOptionsAndSetDefault(this.dorpelCode);
    }

    this.formGroup = this.buildForm();
    this._formComponent = new FormComponent(this.formGroup);
    this.uiStateService.dorpelCode$
      .pipe(
        filter((dorpelCode) => !!dorpelCode),
        takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.formGroup = this.buildForm();
        this.glaslatten = this.filterGlaslattenChoicesByDorpelsoort(this.input.glaslattenChoices, this.dorpelType);
        //we only subscribe to neutenCount field once we know it's enabled
        this.subscribeToNeutenCountChanges();
      });
  }

  private determineUitvoeringAvailability() {
    if (this.uiStateService.currentProductType === "Luvema") {
      this.isUitvoeringAvailable = true;
    }
  }


  private filterGlaslattenChoicesByDorpelsoort(glaslattenOptionsList: GlaslatDto[], currentDorpelType?: string): GlaslatDto[] {
    if (!currentDorpelType) {
      return glaslattenOptionsList;
    }

    let glaslatDorpelsoort = currentDorpelType.toLocaleLowerCase() === "binnendorpel" ? currentDorpelType : (this.isConversionDaagmaat() ? "binnendorpel" : currentDorpelType.toLocaleLowerCase())

    return glaslattenOptionsList.filter(glaslat => glaslat.dorpelsoort.toLocaleLowerCase() === glaslatDorpelsoort);
  }

  private subscribeToNeutenCountChanges(): void {
    this.uiStateService.neutenCount$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((neutenCount) => {
        let enable = !!neutenCount || neutenCount === 0;
        this.enableNeutenDependentFields(enable);
      });
  }

  public ngOnChanges(param) {
    if (param.dorpelType || param.neutDescription) {
      this.glaslatten = this.filterGlaslattenChoicesByDorpelsoort(this.input.glaslattenChoices, this.dorpelType);
    }

    if (this.neutModel) {
      const cachedDaagmaatValue = this.formGroup.get("daagmaat").value;
      this.formGroup = this.buildForm();

      if (cachedDaagmaatValue) {
        //conserve daagmaat value in case it was filled before selecting the neut and rebuilding the form;
        this.uiStateService.updateDaagmaat(cachedDaagmaatValue, this.index);
        this.formGroup.get("daagmaat").setValue(cachedDaagmaatValue);
      }

      this.resetGlaslattenConflictErrors();
      this.enableNeutenDependentFields(!!this.neutModel.omschrijving);
    }

    if (param.neutDescription && this.isConversionDaagmaat()) {
      this.setSponningmaatOptionsAndSetDefault(this.dorpelCode);
      this.uiStateService.populateNeut({
        sponningmaat: this.neutModel?.sponningmaat || this.defaultSponningmaatValue
      }, this.index);
      this.formGroup.get("sponningmaat").setValue(this.neutModel?.sponningmaat || this.defaultSponningmaatValue);
    } else {
      this.sponningmaatOptions = [];
      this.defaultSponningmaatValue = null;
      this.formGroup?.get("sponningmaat").setValue(null)
    }
  }

  public get label(): string {
    return NeutUtils.getLabel(this.index, this.count);
  }

  public getSelectedNeut(): NeutDto {
    if (!this.formGroup) {
      return null;
    }
    const selectedNeutCode = this.formGroup.get('neut');
    if (!selectedNeutCode) {
      return null;
    }
    return this.neuten.find(n => n.neutCode === selectedNeutCode.value);
  }

  public resetNeutCodeSelection(value) {
    if (!this.formGroup) {
      return;
    }
    this.formGroup.get('neut').setValue(value);
  }

  public showError(key: string): boolean {
    return this._formComponent.showError(key);
  }

  public errorMessage(key: string): string {
    const formControl = this.formGroup.get(key);
    if (formControl.valid || formControl.disabled) {
      return '';
    }
    const [firstError] = Object.keys(formControl.errors);
    const error = ValidationMessages[key] ? ValidationMessages[key][firstError] : null;
    return error || this._formComponent.errorMessage(key);
  }

  private neutMatchesDorpelAndPosition(neut: NeutDto, dorpelCode: string): boolean {
    if (neut.dorpelCode !== dorpelCode) {
      return false;
    }
    return neut.positie === NeutUtils.getPositionString(this.neutModel.position);
  }

  private enableNeutenDependentFields(shouldEnable: boolean): void {
    this.neutenDependentFields.forEach(field => {
      let fieldToUpdateValidity = this.formGroup.controls[field];

      if (fieldToUpdateValidity) {
        if (shouldEnable) {
          fieldToUpdateValidity.enable();
        } else {
          fieldToUpdateValidity.disable();
        }
      }
    });
  }

  private buildForm(): FormGroup {
    const formGroup = this.formBuilder.group({
      neut: [{ value: this.neutModel?.neut || '', disabled: true }, [Validators.required]],
      daagmaat: this.neutModel.position !== NeutPosition.Right ? [{ value: this.neutModel?.daagmaat || '', disabled: true }] : null,
      sponningsoort: this.neutModel.position !== NeutPosition.Right ? [{ value: this.neutModel?.sponningsoort || '', disabled: true }] : null,
      maatA: this.neutModel.position === NeutPosition.Left ? [{ value: this.neutModel?.maatA || 0, disabled: true }, [Validators.required]] : null,
      maatZ: this.neutModel.position === NeutPosition.Right ? [{ value: this.neutModel?.maatZ || 0, disabled: true }, [Validators.required]] : null,
      glaslat: [{ value: this.neutModel?.glaslat || '', disabled: true }],
      sponningmaat: [{ value: this.neutModel?.sponningmaat, disabled: true }],
      beglazingsprofiel: [{ value: this.neutModel?.beglazingsprofiel || '', disabled: true }],
      aanslagprofiel: [{ value: this.neutModel?.aanslagprofiel || '', disabled: true }],
      gemonteerd: [{ value: this.neutModel?.gemonteerd || false, disabled: true }],
      damverbreeding: [{ value: this.neutModel?.damverbreeding || 0, disabled: true }],
      dubbelAanslagprofiel: [{ value: this.neutModel?.dubbelAanslagprofiel || 0, disabled: true}],
      position: this.neutModel.position
    });

    return formGroup;
  }

  public onMaatAChanged(): void {
    const maatA = this.formGroup.get('maatA').value;
    this.uiStateService.setMaatA(maatA);
  }

  public onMaatZChanged(): void {
    const maatZ = this.formGroup.get('maatZ').value;
    this.uiStateService.setMaatZ(maatZ);
  }

  public onDaagmaatChanged(index: number): void {
    const daagmaat = this.formGroup.get('daagmaat').value;
    this.uiStateService.updateDaagmaat(daagmaat, index);
  }

  private resetAanslagprofiel(index): void {
    let aanslagprofielField = this.formGroup.get("aanslagprofiel");
    if (aanslagprofielField.value) {
      this.uiStateService.updateAanslagprofiel(null, index);
      aanslagprofielField.setValue("");
      this.aanslagConflictError = GLASLATTEN_CONFLICT_ERR_MESSAGE;
    }
  }

  private resetBeglazingsprofiel(index): void {
    let beglazingsField = this.formGroup.get("beglazingsprofiel");
    if (beglazingsField.value) {
      this.uiStateService.updateBeglazingsprofiel(null, index);
      beglazingsField.setValue("");
      this.beglazingConflictError = GLASLATTEN_CONFLICT_ERR_MESSAGE;
    }
  }

  private resetGlaslat(index): void {
    let glaslatField = this.formGroup.get("glaslat");
    if (glaslatField.value) {
      this.uiStateService.updateGlaslat(null, index);
      glaslatField.setValue("");
      this.glaslatConflictError = GLASLATTEN_CONFLICT_ERR_MESSAGE;
    }
  }

  private resetGlaslattenConflictErrors(): void {
    this.beglazingConflictError = null;
    this.aanslagConflictError = null;
    this.glaslatConflictError = null;
  }

  public onGlaslatChanged(index: number): void {
    const glaslat = this.formGroup.get('glaslat').value;
    this.uiStateService.updateGlaslat(glaslat, index);
    this.resetGlaslattenConflictErrors();
    this.resetAanslagprofiel(index);
    // this.resetBeglazingsprofiel(index);
    this.uiStateService.triggerRedraw();
  }

  public onBeglazingsprofielChanged(index: number): void {
    const beglazingsprofiel = this.formGroup.get('beglazingsprofiel').value;
    this.uiStateService.updateBeglazingsprofiel(beglazingsprofiel, index);
    this.resetGlaslattenConflictErrors();
    // this.resetGlaslat(index);
    this.resetAanslagprofiel(index);
    this.uiStateService.triggerRedraw();
  }

  public onAanslagprofielChanged(index: number): void {
    const aanslagprofiel = this.formGroup.get('aanslagprofiel').value;
    this.uiStateService.updateAanslagprofiel(aanslagprofiel, index);
    this.resetGlaslattenConflictErrors();
    this.resetGlaslat(index);
    this.resetBeglazingsprofiel(index);
    this.uiStateService.triggerRedraw();
  }

  public onDamverbreedingChanged(index: number): void {
    const damverbreeding = this.formGroup.get('damverbreeding').value;
    this.uiStateService.updateDamverbreeding(damverbreeding, index);
  }

  public onSponningsoortChanged(index: number): void {
    const sponningsoort = this.formGroup.get('sponningsoort').value;
    this.uiStateService.updateSpooningsoort(sponningsoort, index);
    this.uiStateService.triggerRedraw();
  }

  public isConversionDaagmaat(): boolean {
    let isConversionDaagmaat = this.neutModel?.omschrijving && (NeutUtils.isW1(this.neutModel?.omschrijving) || NeutUtils.isW3(this.neutModel?.omschrijving) || NeutUtils.isW5(this.neutModel?.omschrijving) || NeutUtils.isDKMidLeft(this.neutModel.omschrijving));
    return this.dorpelType === "Buitendorpel" && isConversionDaagmaat;
  }

  public isDKWisselvlak(): boolean {
    let isNeutDraaiKiep = this.neutModel?.omschrijving && NeutUtils.isDK(this.neutModel?.omschrijving);
    return this.dorpelType === "Buitendorpel" && isNeutDraaiKiep && !NeutUtils.isDKMidLeft(this.neutModel.omschrijving);
  }

  public showSponningmaatTooltip(target: HTMLElement): void {
    this.tooltipService.showTooltip(target);
  }

  public hideSponningmaatTooltip(): void {
    this.tooltipService.hideTooltip();
  }

  private setSponningmaatOptionsAndSetDefault(dorpelCode: string) {
    let damLevel = DorpelUtils.getDamLevelFromDorpelCode(dorpelCode);
    let defaultOption = {
      value: damLevel + 10,
      isDefault: true,
    };
    let extraOption = {
      value: damLevel + 15,
      isDefault: false,
    };

    this.sponningmaatOptions = [defaultOption, extraOption];

    if (dorpelCode === "IW-BU.4272.B" || dorpelCode === "IW-BU.4767.B") {
      let exceptionOption = {
        value: damLevel + 20,
        isDefault: false,
      }
      this.sponningmaatOptions.push(exceptionOption);
    }

    this.defaultSponningmaatValue = defaultOption.value; //ssponningmaat value that is already included in tekening string
  }

  public onSponningmaatChanged(index: number) {
    const sponningmaat = this.formGroup.get('sponningmaat').value;

    this.uiStateService.updateSponningmaat(parseInt(sponningmaat), index);
    this.uiStateService.triggerRedraw();
  }
}

const ValidationMessages = {
  maatA: {
    required: 'Selecteer een waarde a.u.b.'
  },
  sponningsoort: {
    required: 'Selecteer een waarde a.u.b.'
  },
  daagmaat: {
    required: 'Selecteer een waarde a.u.b.'
  },
  maatZ: {
    required: 'Selecteer een waarde a.u.b.'
  }
}
